// GENERATED by helm-console/scripts/ensure-icons.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/icons-destroyer.ts
// (itself generated from lucide-static by helm-bridge/scripts/gen-icon-sprite.mjs).
// Regenerated on every console build; edits here are overwritten.
// The console and the release UI share ONE icon set on purpose — see the
// header of ensure-icons.mjs.
// icons-destroyer.ts — DESTROYER's ICON SET.
//
// THE SHARED ID CONTRACT (identical to icons-glass.ts, and enforced by the
// same instrument). A style does not get its own id space: it answers the
// SAME symbol ids the Blueprint sprite defines, so every call site in the app
// is unchanged and a style switch is a sprite swap, not a refactor.
//   1. NO ORPHANS. Every id below exists in the Blueprint sprite. An id only
//      this set defines would render for DESTROYER and blank for everyone
//      else — the silent-blank failure the icons gate exists to refuse.
//   2. NAMED, COUNTED FALLBACK. A set does not have to cover all 104 ids;
//      what it must never do is leave one empty. icons.ts composes the sprite
//      as "this set's glyph where it has one, the Blueprint glyph otherwise",
//      and scripts/check-icon-refs.mjs prints the split every run.
//
// THE DRAWING RULES (spec §6, ruling R21). A 20-unit grid inside the shared
// 24 viewBox (nothing crosses x=2 or x=22, so the set has a 2-unit margin the
// round-cap sets do not), uniform 2px stroke, SQUARE terminals and miter
// joins, zero rounded corners. Weight, caps and joins are NOT set here:
// styles.css pins `.icon { stroke-width: 2; stroke-linecap: butt;
// stroke-linejoin: miter }` under this style, so one rule governs the set.
//
// TWO THINGS MAKE IT UNMISTAKABLE, AND NEITHER IS DECORATION.
//
// 1. STENCIL BRIDGES. Every closed shape carries a ~1.8-unit GAP — the
//    physical bridge a spray stencil needs to hold its centre. It reads as
//    cut from steel rather than drawn. Crucially it changes NO silhouette:
//    the gap sits in a counter, so it is safe on interactive glyphs and
//    belongs there, on every icon-only button, rather than being withheld for
//    the logo (which is destroyer-3's mistake, named in the judging).
//    A closed circle of radius r bridged at the top is written
//    `M{12+g} {y} A{r} {r} 0 1 1 {12-g} {y}` with g = 0.9 and
//    y = 12 - sqrt(r^2 - g^2) — the arc goes the long way round and simply
//    never closes.
//
// 2. REDUCED MIL-STD-2525 TACTICAL SYMBOLOGY. Diamonds, squares and chevrons
//    instead of circles and blobs, and EACH OF THE FOUR INTENTS OWNS A
//    SILHOUETTE:
//      neutral      a plain square-cornered glyph
//      additive     a chevron pointing OUT of a bracket        (`circle-check`)
//      destructive  the glyph with a hazard-chevron notch cut
//                   from its lower-right                        (`trash-2`, `ban`)
//      error        the glyph inside a DIAMOND                   (`circle-alert`, `siren`)
//    That is what lets DESTROYER survive Battle Lanterns' hue collapse: under
//    a monochrome red field the four intents are still four shapes.

/** The mark: a stencil chevron ("make way" — the bow wave) inside a SQUARED
 * ROUNDEL, the hull-number plaque silhouette (R22; the circle is Glass's, the
 * hexagon Starship's). The roundel carries its bridge on the top edge, so the
 * plaque never closes on itself. One continuous `currentColor` stroke — it
 * re-tints per theme with zero per-theme art. */
const ROUNDEL_CHEVRON =
  '<path d="M12.9 3H16l5 5v8l-5 5H8l-5-5V8l5-5h3.1" />' +
  '<path d="m7.6 15.2 4.4-6.6 4.4 6.6" />' +
  '<path d="M9.7 18.2h4.6" />';

/** FOUR PAIRS THE BLUEPRINT SPRITE DRAWS WITH ONE PIECE OF ART (gen-icon-
 * sprite.mjs maps both ids of each pair onto the same lucide file). The set
 * keeps each pair identical to ITSELF for the same reason it keeps helm and
 * ship-wheel identical: two ids that render the same glyph everywhere else
 * must not diverge under one style, or a call site changes meaning with the
 * picker. Declared as consts so the two entries cannot drift apart in an edit. */
const CLIPBOARD_CHECK =
  '<path d="M12.9 20.6h5.7V5.4H5.4v15.2h5.7" />' +
  '<path d="M9 3.4h6v3.6H9z" />' +
  '<path d="m8.4 13 2.6 2.6 4.6-5.2" />';
/** Two pages held apart at the spine — the stencil bridge IS the gutter. */
const BOOK_OPEN =
  '<path d="M11.4 7.8 3.4 5.4v12.6l8 2.4" />' +
  '<path d="M12.6 7.8 20.6 5.4v12.6l-8 2.4" />' +
  '<path d="M12 8.8v3.4" /> <path d="M12 15.2v3.6" />';
const SCROLL_TEXT =
  '<path d="M12.9 3.4h5.7v13.2H5.4V3.4h5.7" />' +
  '<path d="M3.4 16.6h17.2v2.2l-1.8 1.8H5.2l-1.8-1.8z" />' +
  '<path d="M8.6 7.2h6.8" /> <path d="M8.6 10.4h6.8" /> <path d="M8.6 13.6h4.2" />';
/** The one true circle in the control family: a bearing ring re-run clockwise,
 * bridged at the top like every other closed counter in the set. */
const ROTATE_CW =
  '<path d="M20.4 12a8.4 8.4 0 1 1-2.86-6.32" />' +
  '<path d="M20.4 3.6v5.6h-5.6" />';

/**
 * id -> symbol INNER markup (the `<symbol>` wrapper and the viewBox are added
 * by icons.ts, so a set cannot get either wrong).
 */
export const DESTROYER_SYMBOLS: Record<string, string> = {
  // ---- the mark ------------------------------------------------------
  // Blueprint draws `helm` and `ship-wheel` with the same artwork; the set
  // keeps them identical to each other too.
  helm: ROUNDEL_CHEVRON,
  'ship-wheel': ROUNDEL_CHEVRON,

  // ---- the four intent silhouettes -----------------------------------
  // ADDITIVE — a chevron escaping a bracket. "Weapons free": the single
  // authorised launch action on this screen, and the only outward-pointing
  // glyph in the set.
  'circle-check':
    '<path d="M9.4 3.4H3.4v17.2h6" />' +
    '<path d="m13.6 6.8 6 5.2-6 5.2" />' +
    '<path d="M8.6 12h10.4" />',
  'badge-check':
    '<path d="M9.4 3.4H3.4v17.2h6" />' +
    '<path d="m13.6 6.8 6 5.2-6 5.2" />' +
    '<path d="M8.6 12h10.4" />',
  // ERROR — inside a DIAMOND, bridged at the apex. Red always means "something
  // is broken" (R0.1), and the diamond is what says it with the colour off.
  // The dot is a SQUARE, not Lucide's `h.01` zero-length segment: this set is
  // `stroke-linecap: butt`, so a zero-length segment has no cap and no length
  // and paints NOTHING while the rest of the glyph still draws (no paint probe
  // can see it). Same idiom as `circle-help`/`ellipsis` below.
  'circle-alert':
    '<path d="M12.64 3.24 21.4 12 12 21.4 2.6 12l8.76-8.76" />' +
    '<path d="M12 8.2v4.6" /> <path d="M10.9 15.3h2.2v2.2h-2.2z" />',
  siren:
    '<path d="M12.64 3.24 21.4 12 12 21.4 2.6 12l8.76-8.76" />' +
    '<path d="M8.8 14.2v-2.4a3.2 3.2 0 0 1 6.4 0v2.4Z" />' +
    '<path d="M7.2 16.4h9.6" />',
  // DESTRUCTIVE — the glyph with a hazard-chevron notch cut out of its
  // lower-right. Amber, never red (R0.1): "you are about to delete something"
  // is not "something is broken".
  'trash-2':
    '<path d="M4.4 6.4h15.2" /> <path d="M9.4 6.4V3.6h5.2v2.8" />' +
    '<path d="M6.2 6.4v14h8.2l3.4-3.4V6.4" />' +
    '<path d="M17.8 17h-3.4v3.4" />' +
    '<path d="M9.6 10v6.6" /> <path d="M12.6 10v4" />',
  ban:
    '<path d="M4.4 4.4h11.2v11.2" />' +
    '<path d="M4.4 4.4v15.2h11.2" />' +
    '<path d="M19.6 15.6h-4v4" />' +
    '<path d="m6.6 17.4 10.8-10.8" />',
  // NEUTRAL — plain, square-cornered, no bracket, no diamond, no notch.
  'circle-dot':
    '<path d="M3.6 3.6h16.8v16.8H3.6z" />' +
    '<path d="M9.6 9.6h4.8v4.8H9.6z" />',

  // ---- the status ladder: one plaque, seven states -------------------
  // Every rung is the same bridged square-cornered plaque so the ladder reads
  // as one instrument; the MARK inside it is the state. Under Battle
  // Lanterns, where the ladder is re-pinned onto a luminance ramp, this
  // silhouette is the channel colour no longer carries.
  'st-ring':
    '<path d="M12.9 3.4h7.7v17.2H3.4V3.4h8.2" />',
  'st-half':
    '<path d="M12.9 3.4h7.7v17.2H3.4V3.4h8.2" />' +
    '<path d="M12 7.2v9.6" /> <path d="M12 7.2h4.8v9.6H12" />',
  'st-check':
    '<path d="M12.9 3.4h7.7v17.2H3.4V3.4h8.2" />' +
    '<path d="m7.6 12.2 3 3 5.8-6" />',
  'st-cross':
    '<path d="M12.9 3.4h7.7v17.2H3.4V3.4h8.2" />' +
    '<path d="m8.2 8.2 7.6 7.6" /> <path d="m15.8 8.2-7.6 7.6" />',
  'st-pause':
    '<path d="M12.9 3.4h7.7v17.2H3.4V3.4h8.2" />' +
    '<path d="M9.8 8.4v7.2" /> <path d="M14.2 8.4v7.2" />',
  'st-dash':
    '<path d="M12.9 3.4h7.7v4.4" /> <path d="M20.6 12v4.4" />' +
    '<path d="M20.6 20.6h-4.4" /> <path d="M12 20.6H7.6" />' +
    '<path d="M3.4 20.6v-4.4" /> <path d="M3.4 12V7.6" />' +
    '<path d="M3.4 3.4h4.4" />',
  'st-seal':
    '<path d="M12.9 3.4h7.7v17.2H3.4V3.4h8.2" />' +
    '<path d="M12.9 6.6h4.5v10.8H6.6V6.6h4.4" />' +
    '<path d="m9.2 12.2 2.4 2.4 4-4.4" />',

  // ---- the CIC instrument family -------------------------------------
  // The glyphs that carry the thesis: this is the room the style depicts.
  sonar:
    '<path d="M12 12V2.6" /> <path d="M12 12h9.4" />' +
    '<path d="M12 4.6h5.6" /> <path d="M12 8h9" opacity=".55" />' +
    '<path d="M12 12 4.6 19.4" />' +
    '<path d="M6.6 12H2.6" /> <path d="M12 21.4V17" />',
  compass:
    '<path d="M12.9 3.05A9 9 0 1 1 11.1 3.05" />' +
    '<path d="M12 5.6v1.8" /> <path d="M18.4 12h-1.8" />' +
    '<path d="M12 18.4v-1.8" /> <path d="M5.6 12h1.8" />' +
    '<path d="m8.4 15.6 7.2-7.2" /> <path d="M9.8 9.8h4.4v4.4H9.8z" />',
  buoy:
    '<path d="M12.9 3.05A9 9 0 1 1 11.1 3.05" />' +
    '<path d="M9.4 9.4h5.2v5.2H9.4z" />' +
    '<path d="m5.6 5.6 3.8 3.8" /> <path d="m14.6 14.6 3.8 3.8" />' +
    '<path d="m18.4 5.6-3.8 3.8" /> <path d="m9.4 14.6-3.8 3.8" />',
  porthole:
    '<path d="M12.9 3.05A9 9 0 1 1 11.1 3.05" />' +
    '<path d="M12.9 6.68A5.4 5.4 0 1 1 11.1 6.68" opacity=".6" />' +
    '<path d="M3.6 12h4.6" /> <path d="M15.8 12h4.6" />',
  horizon:
    '<path d="M2.6 14.4h18.8" />' +
    '<path d="M12 3v6.4" /> <path d="M8.6 6.4 12 3l3.4 3.4" />' +
    '<path d="M6 18.6h12" /> <path d="M2.6 18.6h1.8" opacity=".6" />' +
    '<path d="M19.6 18.6h1.8" opacity=".6" />',
  gauge:
    '<path d="M3.4 18.4A9.6 9.6 0 0 1 20.6 18.4" />' +
    '<path d="m12 14.4 4.4-5" />' +
    '<path d="M10.6 14.4h2.8v2.8h-2.8z" />' +
    '<path d="M4.9 12.2 6.6 12.8" opacity=".6" /> <path d="M12 4.6v1.8" opacity=".6" />' +
    '<path d="M19.1 12.2 17.4 12.8" opacity=".6" />',
  // FLUKE RE-CUT 2026-09-07. The shipped arc was `M3.4 14.4v2.4a6.6 6.6 0 0
  // 0 17.2 0v-2.4` — a 17.2-unit chord across a 6.6 radius, which SVG cannot
  // draw, so the UA scales the radius to 8.6 and the arc bottoms out at
  // y=25.4: the flukes were CUT FLAT by the viewBox (measured ink bbox
  // y 3.00..24.00, i.e. hard against the edge). An angular V instead — which
  // is the set's own rule anyway, since the arc was one of only two curves
  // here that were not true bridged circles.
  anchor:
    '<path d="M12 8.6v12.8" />' +
    '<path d="M12.9 3.9A2.6 2.6 0 1 1 11.1 3.9" />' +
    '<path d="M7.6 9.4h8.8" />' +
    '<path d="M3.4 13.6v2.8L12 21.4l8.6-5v-2.8" />',
  lighthouse:
    '<path d="M9.4 9.4h5.2l1.6 11.2H7.8z" />' +
    '<path d="M9 6.2h6v3.2H9z" /> <path d="M12 2.6v3.6" />' +
    '<path d="M4.6 7.8h3" /> <path d="M16.4 7.8h3" />',
  telescope:
    '<path d="m3.4 15.6 12.8-7.4" /> <path d="m5.6 19.4 12.8-7.4" />' +
    '<path d="m3.4 15.6 2.2 3.8" /> <path d="m16.2 8.2 2.2 3.8" />' +
    '<path d="M18.9 4.9h3.2v3.2h-3.2z" />' +
    '<path d="m9.6 18.2-1.4 3.2" /> <path d="m12.4 16.6 1.4 4.8" />',
  // SEA RE-CUT 2026-09-07. The shipped crests were quadratic `q`/`t` curves —
  // the only rounded corners in a set whose header rule is "right angles and
  // 45s, zero rounded corners" — and the smooth-continuation `t` segments
  // overshot the box (ink bbox x 2.50..22.50, y 7.25..24.00, clipped bottom
  // and right). Chevron swell instead: same two-row reading, in the language.
  wave:
    '<path d="m2.6 10.4 4.7-3 4.7 3 4.7-3 4.7 3" />' +
    '<path d="m2.6 16.4 4.7-3 4.7 3 4.7-3 4.7 3" opacity=".65" />',
  signal:
    '<path d="M4.4 20.6v-3.2" /> <path d="M8.4 20.6v-6.4" />' +
    '<path d="M12.4 20.6v-9.6" /> <path d="M16.4 20.6v-12.8" />' +
    '<path d="M20.4 20.6V3.4" />',
  flag:
    '<path d="M5.4 21.4V2.6" />' +
    '<path d="M5.4 3.6h13.2l-3 4.4 3 4.4H5.4" />',
  shield:
    '<path d="M12.9 2.8 20.6 5.6v6.2c0 5-3.6 8.2-8.6 9.4-5-1.2-8.6-4.4-8.6-9.4V5.6l7.7-2.8" />' +
    '<path d="m8.6 12 2.6 2.6 4.6-5" />',
  eye:
    '<path d="M2.6 12 12 5.4 21.4 12 12 18.6z" />' +
    '<path d="M12.9 8.72A3.4 3.4 0 1 1 11.1 8.72" />',
  search:
    '<path d="M11.5 4.5A7 7 0 1 1 9.7 4.5" />' +
    '<path d="m15.8 15.8 5.6 5.6" />',

  // ---- the mode pair --------------------------------------------------
  // Two lighting states of the same plate, not a sun and a moon: the day
  // watch is the lamp lit from every side, the night watch is the same lamp
  // dark on one edge. Both bridged.
  sun:
    '<path d="M12.9 7.62A4.6 4.6 0 1 1 11.1 7.62" />' +
    '<path d="M12 2v2.2" /> <path d="M12 19.8V22" />' +
    '<path d="M2 12h2.2" /> <path d="M19.8 12H22" />' +
    '<path d="m4.9 4.9 1.6 1.6" /> <path d="m17.5 17.5 1.6 1.6" />' +
    '<path d="m19.1 4.9-1.6 1.6" /> <path d="m6.5 17.5-1.6 1.6" />',
  moon:
    '<path d="M12.9 3.24A8.8 8.8 0 1 1 11.1 3.24" />' +
    '<path d="M15.2 4.1a9.6 9.6 0 0 0 0 15.8" />',

  // ====================================================================
  // COMPLETION (owner correction 2026-09-07). The 94 ids below were still
  // resolving to BLUEPRINT artwork — Lucide's round-cap, round-join,
  // organic outlines, which under DESTROYER's own rule
  //     :root[data-style="destroyer"] .icon {
  //       stroke-width: 2; stroke-linecap: butt; stroke-linejoin: miter;
  //       fill: none }
  // render legibly but in a foreign hand: soft corners and circles inside a
  // set whose whole thesis is cut steel. They are drawn here to the SAME
  // three rules the 33 above already follow — 20-unit grid inside the 24
  // viewBox, right angles and 45s, a stencil bridge in the dominant closed
  // counter — so that the set is 127/127 its own and the gate needs no
  // COVERAGE_DEBT row for destroyer.
  //
  // FOUR SHARED CONSTS, not four copies: Blueprint draws gate/clipboard-check,
  // book/book-open, scroll/scroll-text and tack/rotate-cw with the SAME art,
  // and this set keeps each pair identical to itself exactly as helm and
  // ship-wheel are (declared below the table, used here).
  //
  // WHERE THE INTENT SILHOUETTES APPLY. The four-silhouette rule above is a
  // rule about VERBS, not about every glyph: `circle-x` (reject a decision)
  // is destructive, so it takes the hazard-chevron notch out of its
  // lower-right corner, the same cut trash-2 and ban carry. `check`, `plus`
  // and `send` are bare marks that sit INSIDE an intent-coloured button —
  // giving them a bracket too would put two additive silhouettes on one
  // control, so they stay plain. Nothing new takes the diamond; the diamond
  // stays reserved for error.
  //
  // NO ROUNDED ANYTHING. The only arcs in the block are true circles
  // (clock/history dials, the two compass-family instruments already above),
  // and every one of them is bridged by the formula in the header.

  // ---- documents, registers, records ---------------------------------
  logbook:
    '<path d="M12.9 3.4h5.7v17.2H6.6V3.4h4.5" />' +
    '<path d="M6.6 3.4H3.6v17.2h3" />' +
    '<path d="M9.6 8.4h6" /> <path d="M9.6 12h6" /> <path d="M9.6 15.6h3.8" />',
  'notebook-tabs':
    '<path d="M12.9 3.4h7.7v17.2H7.4V3.4h3.7" />' +
    '<path d="M7.4 3.4H3.4v17.2h4" />' +
    '<path d="M20.6 8h-4" /> <path d="M20.6 12h-4" /> <path d="M20.6 16h-4" />',
  'clipboard-list':
    '<path d="M12.9 20.6h5.7V5.4H5.4v15.2h5.7" />' +
    '<path d="M9 3.4h6v3.6H9z" />' +
    '<path d="M8.4 11.4h1.8" /> <path d="M12 11.4h3.6" />' +
    '<path d="M8.4 15.4h1.8" /> <path d="M12 15.4h3.6" />',
  // RE-CUT after the 64px contact sheet: four bare rects of stepped height
  // over a rule read as a BAR CHART, not a shelf. Three bridged volumes of
  // unequal height, each banded across its spine, standing ON the shelf line
  // rather than growing out of it.
  library:
    '<path d="M6.4 5.2h1.2v14.2H3.4V5.2h1.2" /> <path d="M3.4 9h4.2" />' +
    '<path d="M12.4 4.4h1.2v15H9.4V4.4h1.2" /> <path d="M9.4 8.2h4.2" />' +
    '<path d="M18.4 6h1.2v13.4h-4.2V6h1.2" /> <path d="M15.4 9.8h4.2" />' +
    '<path d="M2.6 20.6h18.8" />',
  'file-pen':
    '<path d="M18.6 11.8V8.4L13.6 3.4H5.4v17.2h5.4" />' +
    '<path d="M13.4 3.6v5h5" />' +
    '<path d="m20.6 12.8-7 7-3.2.8.8-3.2 7-7z" />',
  'square-pen':
    '<path d="M11.4 4.4H4.4v15.2h15.2v-7.4" />' +
    '<path d="m20.6 5.4-7 7-3.2.8.8-3.2 7-7z" />',
  pencil:
    '<path d="M11.5 8.3 16.4 3.4l4.2 4.2-11 11-5.6 1.4 1.4-5.6 4.9-4.9" />' +
    '<path d="m13.4 6.4 4.2 4.2" />',
  copy:
    '<path d="M12.9 8.6h7.7v12h-12v-12h2.5" />' +
    '<path d="M16 8.6V3.4H3.4v12.6h5.2" />',
  bookmark: '<path d="M12.9 3.4h5.7v17.2L12 15.6l-6.6 5V3.4h5.7" />',
  'folder-open':
    '<path d="M2.8 20.6V5.4h6l2.2 2.8h8.2v3.2" />' +
    '<path d="M14.2 11.4h7l-3 9.2H2.8l2.6-9.2h7" />',
  mail:
    '<path d="M12.9 5.4h7.7v13.2H3.4V5.4h7.7" />' +
    '<path d="m3.4 6.4 8.6 6.4 8.6-6.4" />',
  'message-square': '<path d="M12.9 4.4h7.7v11.6H9.6l-4.6 4.4V16H3.4V4.4h7.7" />',

  // ---- the ship, the yard, the cargo ---------------------------------
  ship:
    '<path d="M12.9 15.4h7.7l-3 5H6.4l-3-5h7.7" />' +
    '<path d="M8.4 15.4v-3.8h6v3.8" /> <path d="M11.4 11.6V5.2" />' +
    '<path d="M11.4 5.2h5.2l-1.8 2 1.8 2h-5.2" />',
  cargo:
    '<path d="M12.9 4.6h7.7v14.8H3.4V4.6h7.7" />' +
    '<path d="M3.4 8.6h17.2" /> <path d="M3.4 15.4h17.2" />' +
    '<path d="M9.8 10.6h4.4v2.8H9.8z" />',
  container:
    '<path d="M12.9 7.4h7.7v9.2H3.4V7.4h7.7" />' +
    '<path d="M6.8 7.4v9.2" /> <path d="M10.2 7.4v9.2" />' +
    '<path d="M13.8 7.4v9.2" /> <path d="M17.2 7.4v9.2" />' +
    '<path d="M6 16.6v2.6" /> <path d="M18 16.6v2.6" />',
  crane:
    '<path d="M12.9 6.6h7.7v10.8H3.4V6.6h7.7" />' +
    '<path d="m7 17.4 5-10.8" /> <path d="m13 17.4 5-10.8" />' +
    '<path d="M6.6 17.4v3" /> <path d="M17.4 17.4v3" />',
  // RE-CUT: with a crossbar halfway down the mast the V-and-stem read as a
  // FIGURE at 24px. Mast, plinth, no crossbar — the dipole over its base.
  antenna:
    '<path d="m4.6 3.6 7.4 7.4 7.4-7.4" /> <path d="M12 11v8" />' +
    '<path d="M8.8 19h6.4v2.4H8.8z" />',
  knot:
    '<path d="M4.4 19.6V12h6.8V6.6h6.4" />' +
    '<path d="M3 18.2h2.8v2.8H3z" /> <path d="M16.4 5.2h2.8v2.8h-2.8z" />',
  crew:
    '<path d="M6.4 4.6h4.8v4.8H6.4z" />' +
    '<path d="M2.8 20.4v-3.2l2.4-3h6.8l2.4 3v3.2" />' +
    '<path d="M15 6.2h3.8V10H15z" />' +
    '<path d="M14.6 20.4v-3.6l1.6-1.8h3.2l1.6 1.8v3.6" />',
  'user-plus':
    '<path d="M7.4 4.2h5.2v5.2H7.4z" />' +
    '<path d="M2.8 20.6v-3.4l2.6-3.2h9.2l1 1.2" />' +
    '<path d="M18.4 13.6v6.4" /> <path d="M15.2 16.8h6.4" />',

  // ---- instruments, plots, readouts ----------------------------------
  chart:
    '<path d="M3.6 3.6v16.8h16.8" />' +
    '<path d="m6.6 16.4 4.2-4.8 3.2 2.6 5.2-6.4" />' +
    '<path d="M15.6 7.8h3.6v3.6" />',
  dividers:
    '<path d="M12 2.6v3" /> <path d="M10.6 5.6h2.8v2.8h-2.8z" />' +
    '<path d="m11.4 8.4-5 12" /> <path d="m12.6 8.4 5 12" />' +
    '<path d="M8.4 15.2h7.2" />',
  activity: '<path d="M2.6 12h4.2l2.4-7 4.4 14 2.4-7h5.4" />',
  hourglass:
    '<path d="M6 3.4h12" /> <path d="M6 20.6h12" />' +
    '<path d="M7 3.4v3.2l5 5.4 5-5.4V3.4" />' +
    '<path d="M7 20.6v-3.2l5-5.4 5 5.4v3.2" />',
  clock:
    '<path d="M12.9 3.05A9 9 0 1 1 11.1 3.05" />' +
    '<path d="M12 6.6V12h4.4" />',
  history:
    '<path d="M3.05 11.1A9 9 0 1 1 3.05 12.9" />' +
    '<path d="m6.6 8.6-3.6 3.4 3.6 3.4" />' +
    '<path d="M12 6.8V12h4.2" />',
  microscope:
    '<path d="M9.2 3.4h4.4v8.2H9.2z" /> <path d="M11.4 11.6v3" />' +
    '<path d="M6.4 14.6h10v2.6h-10z" /> <path d="M3.6 20.6h16.8" />' +
    '<path d="M16.4 17.2v3.4" /> <path d="M6 8h3.2" />',
  'flask-conical':
    '<path d="M9.2 3.4h5.6" />' +
    '<path d="M10.4 3.4v6.4L4.4 20.6h15.2L13.6 9.8V3.4" />' +
    '<path d="M7.6 15.2h8.8" />',
  stethoscope:
    '<path d="M4.6 3.4v5.4l4 4 4-4V3.4" />' +
    '<path d="M3.4 3.4h2.4" /> <path d="M11.4 3.4h2.4" />' +
    '<path d="M8.6 12.8v3.6h7.8" /> <path d="M16.4 14.6h4v3.6h-4z" />',
  scale:
    '<path d="M12 3.2v17.4" /> <path d="M7.4 20.6h9.2" />' +
    '<path d="M4.6 6.6h14.8" />' +
    '<path d="m4.6 6.6-2.4 6.4H7z" /> <path d="m19.4 6.6-2.4 6.4h4.8z" />',
  hammer:
    '<path d="M12.4 8.2 4.6 16v3.4H8l7.8-7.8" />' +
    '<path d="m13.6 3.4 7 7-3.2 3.2-7-7 1.93-1.93" />',
  // RE-CUT: the first pass (a C-head on a separate shaft rect) read as a
  // plug or a lamp at 24px. One continuous outline instead — an open-end
  // spanner whose jaw IS the counter, bridged across the handle's heel.
  wrench:
    '<path d="M12.9 20.6h.7V11.4l2.2-1.8V3.4h-2.4v2.2h-2.8V3.4H8.2v6.2l2.2 1.8v9.2h.7" />',
  bell:
    '<path d="M6.4 16.4v-5.8L12 5.4l5.6 5.2v5.8" />' +
    '<path d="M4.4 16.4h15.2" /> <path d="M12 3v2.4" />' +
    '<path d="M10.2 18.4h3.6v2.2h-3.6z" />',
  puzzle:
    '<path d="M17.3 3.6h2.5v6.8h-1.8v3.4h1.8v6.8H13v-1.8H9.6v1.8H3.6v-6.8h1.8v-3.4H3.6V3.6h6v1.8h3.4V3.6h2.5" />',
  map: '<path d="M2.8 11.1V5.4L9 3.2v15.4l-6.2 2.2v-7.9" /> <path d="m9 3.2 6 2.2v15.4l-6-2.2" /> <path d="m15 5.4 6.2-2.2v15.4L15 20.8" />',

  // ---- structures, planes, machinery ---------------------------------
  house:
    '<path d="M2.8 11.4 12 3.4l9.2 8" />' +
    '<path d="M5.4 13.2v7.4h13.2v-7.4" />' +
    '<path d="M10.2 20.6v-5h3.6v5" />',
  server:
    '<path d="M12.9 3.4h7.7v6.6H3.4V3.4h7.7" />' +
    '<path d="M12.9 14h7.7v6.6H3.4V14h7.7" />' +
    '<path d="M5.8 5.8h2v2h-2z" /> <path d="M5.8 16.4h2v2h-2z" />' +
    '<path d="M16.4 6.8h2.6" /> <path d="M16.4 17.4h2.6" />',
  database:
    '<path d="M12.9 3.74 20.6 6.6 12 9.8 3.4 6.6 11.1 3.74" />' +
    '<path d="M3.4 6.6v10.8l8.6 3.2 8.6-3.2V6.6" />' +
    '<path d="m3.4 12 8.6 3.2 8.6-3.2" />',
  layers:
    '<path d="M12.9 3.88 20.6 8 12 12.6 3.4 8 11.1 3.88" />' +
    '<path d="m3.4 12.6 8.6 4.6 8.6-4.6" />' +
    '<path d="m3.4 16.6 8.6 4.6 8.6-4.6" />',
  box:
    '<path d="M12.9 3.26 20.6 7.2 20.6 16.8 12 21.2 3.4 16.8 3.4 7.2 11.1 3.26" />' +
    '<path d="M12 21.2v-9.6" /> <path d="m3.4 7.2 8.6 4.4 8.6-4.4" />',
  'grid-2x2':
    '<path d="M3.4 3.4h7.4v7.4H3.4z" /> <path d="M13.2 3.4h7.4v7.4h-7.4z" />' +
    '<path d="M3.4 13.2h7.4v7.4H3.4z" /> <path d="M13.2 13.2h7.4v7.4h-7.4z" />',
  'panels-top-left':
    '<path d="M12.9 3.4h7.7v17.2H3.4V3.4h7.7" />' +
    '<path d="M3.4 9h6.4" /> <path d="M9.8 3.4v17.2" />',
  'square-terminal':
    '<path d="M12.9 3.4h7.7v17.2H3.4V3.4h7.7" />' +
    '<path d="m7.4 8.6 3.4 3.4-3.4 3.4" /> <path d="M12.6 15.4h4.4" />',
  terminal: '<path d="m4.4 6.4 5.6 5.6-5.6 5.6" /> <path d="M12 17.6h7.6" />',
  camera:
    '<path d="M6.9 7.4h13.7v12H3.4v-12h1.7" />' +
    '<path d="M8.8 7.4 10.2 4.8h3.6l1.4 2.6" />' +
    '<path d="M9.4 11h5.2v5.2H9.4z" />',
  bug:
    '<path d="M12.9 9.4h3.7v6.2l-4.6 4.6-4.6-4.6V9.4h3.7" />' +
    '<path d="M9 9.4V7.2h6v2.2" />' +
    '<path d="M9.6 4.6 11 6.4" /> <path d="m14.4 4.6-1.4 1.8" />' +
    '<path d="M2.8 12h4.6" /> <path d="M16.6 12h4.6" />' +
    '<path d="m3.8 17.6 3.6-2.6" /> <path d="m20.2 17.6-3.6-2.6" />',
  workflow:
    '<path d="M3.4 3.4h6.4v6.4H3.4z" /> <path d="M14.2 14.2h6.4v6.4h-6.4z" />' +
    '<path d="M6.6 9.8v4.4h10.8" />',

  // ---- the source-control family -------------------------------------
  'git-pull-request':
    '<path d="M4.4 4.4h3.2v3.2H4.4z" /> <path d="M4.4 16.8h3.2V20H4.4z" />' +
    '<path d="M6 7.6v9.2" />' +
    '<path d="M16.4 4.4h3.2v3.2h-3.2z" /> <path d="M18 7.6v8.4" />' +
    '<path d="m15.6 13.6 2.4 2.4 2.4-2.4" />',
  'git-merge':
    '<path d="M4.4 4.4h3.2v3.2H4.4z" /> <path d="M4.4 16.8h3.2V20H4.4z" />' +
    '<path d="M6 7.6v9.2" />' +
    '<path d="M16.4 10.4h3.2v3.2h-3.2z" /> <path d="M16.4 12H6" />',
  'git-branch':
    '<path d="M4.4 3.4h3.2v3.2H4.4z" /> <path d="M4.4 17.4h3.2v3.2H4.4z" />' +
    '<path d="M6 6.6v10.8" />' +
    '<path d="M16.4 3.4h3.2v3.2h-3.2z" /> <path d="M18 6.6v3.8H6" />',

  // ---- controls, verbs, chrome ---------------------------------------
  // NO SELF-DRAWN FRAME (owner ruling 2026-09-09, #1157, with screenshot:
  // "let's change the help icon so that it doesn't have a square around it
  // because that looks silly since we're putting a square around it for the
  // hover state"). This id used to open with the same full-bleed bridged
  // square every panel glyph in the set carries — and under DESTROYER the
  // icon button's HOVER state is the target-lock reticle, four corner
  // brackets drawn around the button, so the rail read as a box inside a box.
  // Dropping the frame moves the glyph ONTO this set's rule rather than off
  // it: the NEUTRAL silhouette is "a plain square-cornered glyph", and the
  // frame was never carrying an intent. The mark and the square dot are
  // unchanged, so the hook still reads as a question at 16px.
  // Its two rail siblings were checked and carry no frame of their own:
  // `bell` is an outline bell, `gear` an outline gear.
  // THE MARK IS RE-CUT ONTO THE SET'S 20-UNIT GRID, not merely un-framed.
  // The old hook was sized to sit INSIDE the frame (6.4 x 8 units); dropping
  // the frame and keeping it would have replaced a box-in-a-box with a glyph
  // visibly smaller than the `bell` and `gear` beside it in the same rail —
  // one defect for another. Measured in Chromium against both siblings at
  // 18px and 84px: the re-cut (9.6 x 16 units, still inside the set's 2-unit
  // margin) matches their ink; the un-framed original did not. This is
  // ARTWORK on a 24 viewBox, not a size token — the `.ibtn` glyph tokens and
  // the padding rules are another lane's and are untouched here.
  // The 1.4-unit gap between the hook's stem and the dot is this set's
  // stencil bridge, and the dot stays a SQUARE (the set is
  // `stroke-linecap: butt`, so a zero-length `h.01` dot would paint nothing).
  'circle-help':
    '<path d="M7.2 8.6V4.6h9.6v5.4L12 14.2v1.8" />' +
    '<path d="M10.4 17.4h3.2v3.2h-3.2z" />',
  // MITER LIMIT, not rounding. A 2px miter join on a 40-degree star point
  // runs ~2 units past the vertex (measured: assistant, send and zap were the
  // three glyphs whose ink reached the 24-viewBox edge), which is a needle,
  // not a cut edge. `stroke-miterlimit="2"` bevels exactly those joins and
  // leaves every right angle in the set untouched — the tip reads as sheared
  // steel, which is the idiom. The style's CSS sets linejoin but no limit,
  // so the presentation attribute is the seat with nothing to fight.
  assistant:
    '<path stroke-miterlimit="2" d="M12.9 6.14 14.2 9.8 20.4 12 14.2 14.2 12 20.4 9.8 14.2 3.6 12 9.8 9.8 11.1 6.14" />' +
    '<path stroke-miterlimit="2" d="m5.6 15.8.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8z" />',
  play: '<path d="M6.6 11.1V3.6L19.4 12 6.6 20.4v-7.5" />',
  // STOP. Two bridged squares two units apart, so the 2px strokes MEET and
  // the rim paints as one solid 4-unit band — the stop block, not an
  // outline. The 3-unit version of this measured as a thin frame around a
  // medium square on the contact sheet, i.e. indistinguishable from
  // `circle-dot` (a thin frame around a small pip), which is the neutral
  // marker sitting a few pixels away in the same rows.
  square:
    '<path d="M12.9 4.6h6.5v14.8H4.6V4.6h6.5" />' +
    '<path d="M12.9 6.6h4.5v10.8H6.6V6.6h4.5" />',
  chev: '<path d="m9.4 5.4 6.6 6.6-6.6 6.6" />',
  'chevron-left': '<path d="m14.6 5.4-6.6 6.6 6.6 6.6" />',
  'chevron-down': '<path d="m5.4 9 6.6 6.6L18.6 9" />',
  'chevron-up': '<path d="m5.4 15 6.6-6.6 6.6 6.6" />',
  'chevrons-right': '<path d="m5 5.4 6.6 6.6L5 18.6" /> <path d="m12.4 5.4 6.6 6.6-6.6 6.6" />',
  'arrow-left': '<path d="M20.4 12H3.6" /> <path d="m10.2 5.4-6.6 6.6 6.6 6.6" />',
  'arrow-right': '<path d="M3.6 12h16.8" /> <path d="m13.8 5.4 6.6 6.6-6.6 6.6" />',
  'arrow-up-right': '<path d="M7 17 17 7" /> <path d="M8.4 7H17v8.6" />',
  // NEUTRAL, AND THEREFORE UNFRAMED (#1169 — the sweep #1167 named rather than
  // took). This opened with the very square `circle-help` shed: under this
  // style an icon button's HOVER state is the target-lock reticle, four corner
  // brackets drawn AROUND the button, so a glyph carrying its own frame renders
  // as a box inside a box. The frame was never carrying an intent — this set's
  // NEUTRAL silhouette is "a plain square-cornered glyph" (R0.4) — so removing
  // it moves the glyph ONTO the set's own rule rather than off it.
  // RE-CUT, not merely un-framed, which is the half of #1167 that is easiest to
  // lose: the old arrow was drawn to sit INSIDE the frame's counter (8.8 x 9.8)
  // and un-framing it alone would leave it visibly small beside `arrow-right`
  // and `plus` in the same rail — one defect traded for another. This is
  // `arrow-right`'s own geometry turned through 90 degrees: a 16.8-unit shaft
  // and a 13.2-unit head, inside the set's 2-unit margin.
  'circle-arrow-up':
    '<path d="M12 20.4V3.6" /> <path d="m5.4 10.2 6.6-6.6 6.6 6.6" />',
  plus: '<path d="M12 4.4v15.2" /> <path d="M4.4 12h15.2" />',
  x: '<path d="m5.4 5.4 13.2 13.2" /> <path d="m18.6 5.4-13.2 13.2" />',
  check: '<path d="m4.6 12.4 5 5L19.4 6.6" />',
  // DESTRUCTIVE: reject/dismiss a decision, so it carries the same
  // hazard-chevron notch cut out of the lower-right that trash-2 and ban do.
  // UNFRAMED (#1169), for the same reticle reason as `circle-arrow-up` above —
  // and here the NOTCH is what survives the frame, not the other way round. It
  // is this set's DESTRUCTIVE intent channel (R0.4: under Battle Lanterns' hue
  // collapse the four intents are still four shapes), and it is also the only
  // thing keeping this id distinct from the plain `x` four lines up, which a
  // bare un-framed X would have duplicated exactly. So the cut moves off the
  // frame and onto the MARK: the X is re-cut from the frame counter's 7.2 units
  // to `x`'s own 13.2, and its lower-right arm stops 1.4 units short of the
  // notch vertex — this set's stencil-bridge gap, the same one that sits
  // between `circle-help`'s hook and its dot.
  'circle-x':
    '<path d="m5.4 5.4 8.2 8.2" /> <path d="m18.6 5.4-13.2 13.2" />' +
    '<path d="M18.6 14.6h-4v4" />',
  ellipsis:
    '<path d="M3.6 10.4h3.2v3.2H3.6z" /> <path d="M10.4 10.4h3.2v3.2h-3.2z" />' +
    '<path d="M17.2 10.4h3.2v3.2h-3.2z" />',
  pin:
    '<path d="M12.9 3.4h2.7v5.4l2.4 4.4H6l2.4-4.4V3.4h2.7" />' +
    '<path d="M12 13.2v7.4" />',
  star:
    '<path stroke-miterlimit="2" d="M12.9 6.2 14.1 9.7 20.6 9.8 15.4 13.7 17.3 19.9 12 16.2 6.7 19.9 8.6 13.7 3.4 9.8 9.9 9.7 11.1 6.2" />',
  zap: '<path stroke-miterlimit="2" d="M9.6 7.3 13.6 2.6 12.4 10.6 18.8 10.6 9.6 21.4 10.8 13.4 4.4 13.4 8.4 8.7" />',
  send:
    '<path stroke-miterlimit="2" d="M12.9 6.4 21.4 3.4 14 21.2 10.4 13.6 2.8 10 11.3 7" />' +
    '<path d="m10.4 13.6 11-10.2" />',
  download:
    '<path d="M12 3.4v11.6" /> <path d="m6.6 10.6 5.4 5.4 5.4-5.4" />' +
    '<path d="M3.6 19.4h16.8" />',
  upload:
    '<path d="M12 16V4.4" /> <path d="m6.6 9.8 5.4-5.4 5.4 5.4" />' +
    '<path d="M3.6 19.4h16.8" />',
  'log-in':
    '<path d="M14.6 3.4h4v17.2h-4" />' +
    '<path d="m8.4 7.6 4.4 4.4-4.4 4.4" /> <path d="M2.8 12h10" />',
  'log-out':
    '<path d="M9.4 3.4h-4v17.2h4" />' +
    '<path d="m15.2 7.6 4.4 4.4-4.4 4.4" /> <path d="M9 12h10.6" />',
  'external-link':
    '<path d="M11.4 4.4H4.4v15.2h15.2v-7" />' +
    '<path d="M13.6 3.4h7v7" /> <path d="m11.6 12.4 9-9" />',
  'undo-2':
    '<path d="m9 6.4-5.6 5.6L9 17.6" /> <path d="M3.4 12h12v8h-4.4" />',
  reply: '<path d="m9 6.4-5.6 5.6L9 17.6" /> <path d="M3.4 12h12v8" />',
  filter: '<path d="M12.9 4.4h8.3l-7 8.6v7.6l-4.4-2.2v-5.4l-7-8.6h8.3" />',
  link:
    '<path d="M9.6 8.4H5.4v7.2h4.2" /> <path d="M14.4 8.4h4.2v7.2h-4.2" />' +
    '<path d="M8.4 12h7.2" />',
  'maximize-2':
    '<path d="M13.6 3.6h6.8v6.8" /> <path d="M10.4 20.4H3.6v-6.8" />' +
    '<path d="m20.4 3.6-6.4 6.4" /> <path d="M3.6 20.4 10 14" />',
  'minimize-2':
    '<path d="M10.4 3.6v6.8H3.6" /> <path d="M13.6 20.4v-6.8h6.8" />' +
    '<path d="m3.6 3.6 6.8 6.8" /> <path d="m20.4 20.4-6.8-6.8" />',
  'list-restart':
    '<path d="M9.6 5h11" /> <path d="M9.6 12h11" /> <path d="M9.6 19h11" />' +
    '<path d="M6.6 3.4v5.4H2.6" /> <path d="m4.6 6.4-2 2.4 2 2.4" />',
  'refresh-cw':
    '<path d="M20.4 9.4A8.4 8.4 0 0 0 6 6.1L2.6 9.3" />' +
    '<path d="M2.6 3.6v5.7h5.7" />' +
    '<path d="M3.6 14.6a8.4 8.4 0 0 0 14.4 3.3l3.4-3.2" />' +
    '<path d="M21.4 20.4v-5.7h-5.7" />',
  gear:
    '<path d="M12.9 3.4h1.5v2.4h4.4v3.8h2.4v4.8h-2.4v3.8h-4.4v2.4H9.6v-2.4H5.2v-3.8H2.8V9.6h2.4V5.8h4.4V3.4h1.5" />' +
    '<path d="M9.6 9.6h4.8v4.8H9.6z" />',

  // ---- the four one-art pairs (consts above) --------------------------
  gate: CLIPBOARD_CHECK,
  'clipboard-check': CLIPBOARD_CHECK,
  book: BOOK_OPEN,
  'book-open': BOOK_OPEN,
  scroll: SCROLL_TEXT,
  'scroll-text': SCROLL_TEXT,
  tack: ROTATE_CW,
  'rotate-cw': ROTATE_CW,

  // ── release-scope SECTION TYPES (#810 PR 4): one per renderer family,
  // cut in this set's own construction so the corpus TOC's section rows are
  // own-set under every style (icons:check refuses a fallback).
  'rs-prose':
    '<path d="M12.9 4.4h5.5v15.2H5.6V4.4h5.5" /> <path d="M8.6 9h6.8" /> <path d="M8.6 12.4h6.8" />' +
    ' <path d="M8.6 15.8h4.2" />',
  'rs-paired-lists':
    '<path d="M7.6 4.6h2.8v14.8H3.2V4.6h2.6" /> <path d="M18.2 4.6H21v14.8h-7.2V4.6h2.6" />' +
    ' <path d="M5.4 9.2h2.6" /> <path d="M5.4 12.8h2.6" /> <path d="M15.8 9.2h2.6" />' +
    ' <path d="M15.8 12.8h2.6" />',
  'rs-table':
    '<path d="M12.9 4.4h7.7v15.2H3.4V4.4h7.7" /> <path d="M3.4 9.4h17.2" /> <path d="M12 9.4v10.2" />' +
    ' <path d="M3.4 14.6h17.2" />',
  'rs-table-expansion':
    '<path d="M12.9 3.6h7.7v9H3.4v-9h7.7" /> <path d="M3.4 8.2h17.2" />' +
    ' <path d="M12.9 15.2h5.3v5.4H5.8v-5.4h5.3" /> <path d="m10.2 10 1.8 1.8 1.8-1.8" />',
  'rs-rules':
    '<path d="M4.4 5.4h2.6v2.6H4.4z" /> <path d="M4.4 10.7h2.6v2.6H4.4z" />' +
    ' <path d="M4.4 16h2.6v2.6H4.4z" /> <path d="M10.4 6.7h10.2" /> <path d="M10.4 12h10.2" />' +
    ' <path d="M10.4 17.3h6.6" />',
  'rs-state-machine':
    '<path d="M6.9 8.4h2.7v7.2H2.4V8.4h2.7" /> <path d="M18.9 8.4h2.7v7.2h-7.2V8.4h2.7" />' +
    ' <path d="M9.6 12h4.8" /> <path d="m12.6 10 2 2-2 2" />',
  'rs-spec-cards':
    '<path d="M13.9 8.4h6.7v12.2H5.4V8.4h6.7" /> <path d="M3.4 15.4V3.4h11.8" />' +
    ' <path d="M8.6 12.6h8.6" /> <path d="M8.6 16.2h5.4" />',
  'rs-wireframe':
    '<path d="M12.9 4.4h7.7v15.2H3.4V4.4h7.7" /> <path d="M3.4 8.6h17.2" />' +
    ' <path d="M10.3 11.6h2.1v4.8h-6v-4.8h2.1" /> <path d="M15.2 12.4h2.8" />' +
    ' <path d="M15.2 15.4h2.8" />',
};

/** Every id this set re-cuts. Sorted so the gate's output is stable. */
export const DESTROYER_ICON_IDS: string[] = Object.keys(DESTROYER_SYMBOLS).sort();
